import { ShortCodeService } from '@ht/lib-plural-vue';
import types from '@/actions/types';
import useKnowledgeBase from '@/composables/useKnowledgeBase';
import useConfig from '@/composables/useConfig';
import useSequence from '@/composables/useSequence';
import Base from '@/actions/base';
import useAnalytics from '@/composables/useAnalytics';

export default class KnowledgeBase extends Base {
  constructor(params) {
    super(params, types.KNOWLEDGE_BASE);

    this.stopped = false;
    this.paused = false;
    this.shouldComplete = null;
    this.customPrompt = params.customPrompt;
  }

  handleError(err) {
    const { runSequence } = useSequence();
    const { setFlowStateBackToStart } = useConfig();

    if (!this.paused) {
      this.resolve(err);

      if (!this.stopped && this.params.failureFlow) {
        setFlowStateBackToStart(this.params.failureFlow);
        runSequence(this.params.failureFlow);
      }
    } else {
      this.shouldComplete = {
        result: err,
        next: !this.stopped && this.params.failureFlow ? this.params.failureFlow : null,
      };
    }
  }

  execute() {
    const { runSequence } = useSequence();
    const { askQuestion } = useKnowledgeBase();
    const { getVariable, setFlowStateBackToStart } = useConfig();
    const { trackAction } = useAnalytics();
    trackAction(this);

    const {
      knowledgeBaseId,
      knowledgeBaseVariableId,
      successFlow,
      variableId,
    } = this.params;
    const resolvedKnowledgeBaseId = knowledgeBaseId
      || (knowledgeBaseVariableId && getVariable(knowledgeBaseVariableId)?.value);

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      this.resolve = resolve;

      if (!resolvedKnowledgeBaseId || (!variableId && !this.customPrompt)) {
        return;
      }

      const question = getVariable(variableId)?.value
        || new ShortCodeService(this.customPrompt || '').matchAttributes().getValue();

      if (!question) {
        return;
      }

      const { text, error } = await askQuestion({
        knowledgeBaseId: resolvedKnowledgeBaseId,
        question,
      });

      if (error) {
        this.handleError({ error: true, message: error });
        return;
      }

      if (!this.paused) {
        this.resolve(text);
        if (successFlow) {
          setFlowStateBackToStart(successFlow);
          runSequence(successFlow);
        }
      } else {
        this.shouldComplete = {
          result: text,
          next: successFlow,
        };
      }
    });
  }

  stop() {
    this.stopped = true;
    this.resolve();
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    if (this.shouldComplete) {
      this.resolve(this.shouldComplete.result);
      if (this.shouldComplete.next) {
        const { runSequence } = useSequence();
        const { setFlowStateBackToStart } = useConfig();

        setFlowStateBackToStart(this.shouldComplete.next);
        runSequence(this.shouldComplete.next);
      }
    }
  }
}
